import type {
  CouponHoldingSegment,
  CouponHoldingSegmentState,
  LotteryEvent,
  SegmentSpec,
} from '@kawai/shared';

/**
 * M-166: セグメント条件「クーポン」の選択肢（純関数）。
 *
 * 複製クーポン（#01〜#10）は人に見せず、ルーレットの**用途**（参加賞 / 1〜4 等）を 1 つの
 * 選択肢にまとめる。`couponIds` がその OR。顧客向けの呼び名に合わせて全員分は「参加賞」
 * （スタッフ向け「当選クーポン」ページの「全員」列はそのまま）。
 */
export interface CouponSegmentOption {
  /** select の value（イベント内で一意 / 保存はしない）。 */
  value: string;
  /** select の表示（用途 ─ クーポン名）。 */
  label: string;
  /** `SegmentSpec.couponHolding.label` に保存する短い表記（要約用）。 */
  shortLabel: string;
  couponIds: string[];
}

export function lotteryCouponSegmentOptions(
  event: LotteryEvent | null | undefined,
): CouponSegmentOption[] {
  if (!event) return [];
  const out: CouponSegmentOption[] = [];
  if (event.everyone.couponIds.length > 0) {
    out.push({
      value: 'everyone',
      label: `参加賞 ─ ${event.everyone.title}`,
      shortLabel: '参加賞',
      couponIds: [...event.everyone.couponIds],
    });
  }
  const prizes = [...event.prizes].sort((a, b) => a.grade - b.grade);
  for (const p of prizes) {
    if (p.couponIds.length === 0) continue;
    out.push({
      value: `prize:${p.id}`,
      label: `${p.grade}等 ─ ${p.title}`,
      shortLabel: `${p.grade}等 ${p.title}`,
      couponIds: [...p.couponIds],
    });
  }
  return out;
}

export const COUPON_HOLDING_STATE_LABELS: Readonly<
  Record<CouponHoldingSegmentState, string>
> = {
  claimed_unused: '取得・未使用',
  claimed: '取得',
  redeemed: '使用済み',
};

export const COUPON_HOLDING_STATE_OPTIONS: ReadonlyArray<{
  value: CouponHoldingSegmentState;
  label: string;
}> = [
  { value: 'claimed_unused', label: '取得して未使用' },
  { value: 'claimed', label: '取得（使用済みも含む）' },
  { value: 'redeemed', label: '使用済み' },
];

/** 現在の条件に一致する選択肢（couponIds の集合一致）。マスタ外なら undefined。 */
export function findCouponSegmentOption(
  options: ReadonlyArray<CouponSegmentOption>,
  spec: CouponHoldingSegment | undefined,
): CouponSegmentOption | undefined {
  if (!spec) return undefined;
  const key = [...spec.couponIds].sort().join('\n');
  return options.find((o) => [...o.couponIds].sort().join('\n') === key);
}

/** 選択肢 + 状態から保存する条件を組む（非破壊）。 */
export function buildCouponHoldingSegment(
  option: CouponSegmentOption,
  state: CouponHoldingSegmentState,
): CouponHoldingSegment {
  return { couponIds: [...option.couponIds], state, label: option.shortLabel };
}

/** 要約に使う表記（label 無し = 旧データや外部入力は枚数で表す）。 */
export function describeCouponHolding(spec: SegmentSpec['couponHolding']): string | null {
  if (!spec || spec.couponIds.length === 0) return null;
  const subject = spec.label ?? `クーポン ${spec.couponIds.length} 種`;
  return `クーポン=${subject}（${COUPON_HOLDING_STATE_LABELS[spec.state]}）`;
}
